import { useState, useCallback } from 'react';

export interface CollectionFiltersState {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface UseCollectionFiltersReturn {
  search: string;
  page: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  paging: CollectionFiltersState;
  
  // Actions
  setSearch: (query: string) => void;
  setPage: (newPage: number) => void;
  setSortBy: (field: string) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  setPaging: (paging: CollectionFiltersState) => void;
  
  // Toggle sort (same field toggles order)
  toggleSort: (field: string) => void;
  
  // Reset to defaults
  reset: () => void;
}

/**
 * Hook to manage collection search, sorting, and pagination state
 * Helps consolidate filtering logic and avoid prop drilling
 */
export const useCollectionFilters = (
  defaultSortBy: string = 'createdAt',
  defaultSortOrder: 'asc' | 'desc' = 'desc'
): UseCollectionFiltersReturn => {
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
  const [paging, setPaging] = useState<CollectionFiltersState>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const toggleSort = useCallback((field: string) => {
    if (sortBy === field) {
      // Toggle sort order if same column clicked
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Switch to new field, default to desc
      setSortBy(field);
      setSortOrder('desc');
    }
    // Reset to page 1 when sorting changes
    setPage(1);
  }, [sortBy, sortOrder]);

  const reset = useCallback(() => {
    setSearch('');
    setPage(1);
    setSortBy(defaultSortBy);
    setSortOrder(defaultSortOrder);
    setPaging({
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
    });
  }, [defaultSortBy, defaultSortOrder]);

  return {
    search,
    page,
    sortBy,
    sortOrder,
    paging,
    
    setSearch: (query: string) => {
      setSearch(query);
      setPage(1); // Reset to page 1 on search
    },
    setPage,
    setSortBy,
    setSortOrder,
    setPaging,
    toggleSort,
    reset,
  };
};

export default useCollectionFilters;
